import React, { useState, useEffect } from 'react';
import axios from '../../api/axiosConfig';
import './AdminOrders.css';

const AdminOrders = () => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    campaignId: ''
  });
  
  // Selected order for status update
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [adminResponse, setAdminResponse] = useState('');
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(null); // orderId being deleted

  useEffect(() => {
    fetchCampaigns();
    fetchOrders();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [filters]);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get('/api/admin/campaigns');
      if (response.data.success) {
        setCampaigns(response.data.campaigns || []);
      }
    } catch (err) {
      console.error('Error fetching campaigns:', err);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.campaignId) params.append('campaignId', filters.campaignId);

      const response = await axios.get(`/api/orders/admin/all?${params.toString()}`);
      if (response.data.success) {
        setOrders(response.data.orders || []);
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/orders/admin/stats');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedOrder || !newStatus) return;

    setUpdating(true);
    try {
      const response = await axios.patch(
        `/api/orders/admin/${selectedOrder.orderId}/status`,
        {
          status: newStatus,
          adminResponse: adminResponse || null
        }
      );

      if (response.data.success) {
        setShowStatusModal(false);
        setSelectedOrder(null);
        setNewStatus('');
        setAdminResponse('');
        fetchOrders();
        fetchStats();
        alert('Order status updated successfully!');
      }
    } catch (err) {
      console.error('Error updating order status:', err);
      alert(err.response?.data?.error || 'Failed to update order status');
    } finally {
      setUpdating(false);
    }
  };

  const openStatusModal = (order) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setAdminResponse(order.adminResponse || '');
    setShowStatusModal(true);
  };

  const closeStatusModal = () => {
    setShowStatusModal(false);
    setSelectedOrder(null);
    setNewStatus('');
    setAdminResponse('');
  };

  const handleDeleteOrder = async (order) => {
    if (!window.confirm(`Delete order from ${order.agentName} (${order.leadsQuantity} leads, $${order.totalPrice.toFixed(2)})?\n\nThis action cannot be undone.`)) {
      return;
    }
    setDeleting(order.orderId);
    try {
      const response = await axios.delete(`/api/orders/admin/${order.orderId}`);
      if (response.data.success) {
        fetchOrders();
        fetchStats();
        alert('Order deleted successfully.');
      }
    } catch (err) {
      console.error('Error deleting order:', err);
      alert(err.response?.data?.error || 'Failed to delete order');
    } finally {
      setDeleting(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', class: 'status-pending' },
      approved: { label: 'Approved', class: 'status-approved' },
      rejected: { label: 'Rejected', class: 'status-rejected' },
      invoice_sent: { label: 'Invoice Sent', class: 'status-invoice' },
      completed: { label: 'Completed', class: 'status-completed' }
    };

    const config = statusConfig[status] || { label: status, class: 'status-default' };
    return <span className={`status-badge ${config.class}`}>{config.label}</span>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="admin-orders-container">
      <div className="orders-header">
        <h2>Opportunity Orders Management</h2>
        <div className="header-actions">
          <button className="btn-refresh" onClick={() => { fetchOrders(); fetchStats(); }}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card stat-total">
            <div className="stat-label">Total Orders</div>
            <div className="stat-value">{stats.total}</div>
          </div>
          <div className="stat-card stat-pending">
            <div className="stat-label">Pending</div>
            <div className="stat-value">{stats.pending}</div>
          </div>
          <div className="stat-card stat-approved">
            <div className="stat-label">Approved</div>
            <div className="stat-value">{stats.approved}</div>
          </div>
          <div className="stat-card stat-invoice">
            <div className="stat-label">Invoice Sent</div>
            <div className="stat-value">{stats.invoiceSent}</div>
          </div>
          <div className="stat-card stat-completed">
            <div className="stat-label">Completed</div>
            <div className="stat-value">{stats.completed}</div>
          </div>
          <div className="stat-card stat-revenue">
            <div className="stat-label">Total Revenue</div>
            <div className="stat-value">${stats.totalRevenue.toFixed(2)}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label htmlFor="statusFilter">Status:</label>
          <select
            id="statusFilter"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="invoice_sent">Invoice Sent</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="campaignFilter">Campaign:</label>
          <select
            id="campaignFilter"
            value={filters.campaignId}
            onChange={(e) => setFilters({ ...filters, campaignId: e.target.value })}
            className="filter-select"
          >
            <option value="">All Campaigns</option>
            {campaigns.map((campaign) => (
              <option key={campaign.campaignId} value={campaign.campaignId}>
                {campaign.name}
              </option>
            ))}
          </select>
        </div>

        {(filters.status || filters.campaignId) && (
          <button
            className="btn-clear-filters"
            onClick={() => setFilters({ status: '', campaignId: '' })}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Orders Table */}
      <div className="orders-list">
        {loading && <div className="loading">Loading orders...</div>}
        
        {!loading && orders.length === 0 && (
          <div className="no-orders">
            <p>No orders found matching the selected filters.</p>
          </div>
        )}

        {!loading && orders.length > 0 && (
          <div className="orders-table-container">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Agent</th>
                  <th>Campaign</th>
                  <th>Date</th>
                  <th>Leads</th>
                  <th>Price/Lead</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <React.Fragment key={order.orderId}>
                    <tr className="order-row">
                      <td className="order-id">{order.orderId.substring(0, 8)}...</td>
                      <td>
                        <div className="agent-info">
                          <div className="agent-name">{order.agentName}</div>
                          <div className="agent-email">{order.agentEmail}</div>
                        </div>
                      </td>
                      <td>{order.campaignName}</td>
                      <td>{formatDate(order.createdAt)}</td>
                      <td className="text-center">{order.leadsQuantity}</td>
                      <td>${order.pricePerLead.toFixed(2)}</td>
                      <td className="order-total">${order.totalPrice.toFixed(2)}</td>
                      <td>{getStatusBadge(order.status)}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-action btn-update-status"
                            onClick={() => openStatusModal(order)}
                            title="Update Status"
                          >
                            Update
                          </button>
                          <button
                            className="btn-action btn-view-details"
                            onClick={() => {
                              const row = document.getElementById(`details-${order.orderId}`);
                              row.style.display = row.style.display === 'none' ? 'table-row' : 'none';
                            }}
                            title="View Details"
                          >
                            Details
                          </button>
                          <button
                            className="btn-action btn-delete"
                            onClick={() => handleDeleteOrder(order)}
                            disabled={deleting === order.orderId}
                            title="Delete Order"
                            style={{
                              backgroundColor: '#dc3545',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '4px 10px',
                              cursor: deleting === order.orderId ? 'not-allowed' : 'pointer',
                              opacity: deleting === order.orderId ? 0.6 : 1,
                              fontSize: '13px'
                            }}
                          >
                            {deleting === order.orderId ? '...' : '🗑 Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                    <tr id={`details-${order.orderId}`} className="order-details-row" style={{ display: 'none' }}>
                      <td colSpan="9">
                        <div className="order-details">
                          <div className="detail-section">
                            <h4>Order Information</h4>
                            <div className="detail-grid">
                              <div className="detail-item">
                                <label>Order ID:</label>
                                <span className="monospace">{order.orderId}</span>
                              </div>
                              <div className="detail-item">
                                <label>Created:</label>
                                <span>{formatDate(order.createdAt)}</span>
                              </div>
                              {order.updatedAt !== order.createdAt && (
                                <div className="detail-item">
                                  <label>Last Updated:</label>
                                  <span>{formatDate(order.updatedAt)}</span>
                                </div>
                              )}
                              {order.ghlLocationName && (
                                <div className="detail-item">
                                  <label>GHL Location:</label>
                                  <span>{order.ghlLocationName}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {order.note && (
                            <div className="detail-section">
                              <h4>Agent's Note</h4>
                              <p className="order-note">{order.note}</p>
                            </div>
                          )}

                          {order.adminResponse && (
                            <div className="detail-section">
                              <h4>Admin Response</h4>
                              <p className="order-note">{order.adminResponse}</p>
                              {order.adminRespondedBy && (
                                <small>
                                  Responded by: {order.adminRespondedBy} on {formatDate(order.adminRespondedAt)}
                                </small>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Status Update Modal */}
      {showStatusModal && selectedOrder && (
        <div className="modal-overlay" onClick={closeStatusModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Update Order Status</h3>
              <button className="btn-close-modal" onClick={closeStatusModal}>✕</button>
            </div>

            <div className="modal-body">
              <div className="order-summary-modal">
                <div><strong>Agent:</strong> {selectedOrder.agentName}</div>
                <div><strong>Campaign:</strong> {selectedOrder.campaignName}</div>
                <div><strong>Leads:</strong> {selectedOrder.leadsQuantity}</div>
                <div><strong>Total:</strong> ${selectedOrder.totalPrice.toFixed(2)}</div>
              </div>

              {selectedOrder.note && (
                <div className="agent-note-section">
                  <label>Agent's Note:</label>
                  <p>{selectedOrder.note}</p>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="newStatus">
                  New Status <span className="required">*</span>
                </label>
                <select
                  id="newStatus"
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="form-control"
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="invoice_sent">Invoice Sent</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="adminResponse">Admin Response (Optional)</label>
                <textarea
                  id="adminResponse"
                  rows="4"
                  value={adminResponse}
                  onChange={(e) => setAdminResponse(e.target.value)}
                  placeholder="Add a response message for the agent..."
                  className="form-control"
                />
                <small className="form-help">
                  This message will be visible to the agent when they view their order.
                </small>
              </div>

              <div className="status-workflow-info">
                <h4>Status Workflow:</h4>
                <ul>
                  <li><strong>Pending:</strong> Order received, awaiting review</li>
                  <li><strong>Approved:</strong> Order approved, ready to send invoice</li>
                  <li><strong>Rejected:</strong> Order rejected (add reason in response)</li>
                  <li><strong>Invoice Sent:</strong> Invoice sent to agent, awaiting payment</li>
                  <li><strong>Completed:</strong> Payment received, Opportunities will be delivered</li>
                </ul>
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={closeStatusModal}
                disabled={updating}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleStatusUpdate}
                disabled={updating || !newStatus}
              >
                {updating ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
